import React, { FC, useEffect, useState } from 'react';
import { Skeleton } from 'antd';
import { WarningCircle } from 'common/svg';
import { useAppDispatch, useAppSelector } from 'app/store/store.hooks';
import { getMonitorSalesOosModel } from 'app/store/reducers/monitorSales.reducer';
import { IMonitorSalesOosItem, IMonitorSalesParams } from 'entities/MonitorSales/MonitorSales.models';

// ─── Design-space constants (SVG units, scale via viewBox) ────────────────────
// Everything is computed in this coordinate system; the SVG scales to fill
// its container via width="100%" and the viewBox.

const DESIGN = 280;
const DCX = DESIGN / 2;
const DCY = DESIGN / 2;
const INNER_MIN = 24;
const INNER_MAX = 48;
const OUTER_SCALE = 1.55;
const OUTER_OPACITY = 0.22;
const LINE_LEN = 35;
const PILL_W = 74;
const PILL_H = 20;
const PILL_RX = 10;
const FONT_NAME = 12;
const FONT_CAT = 10;
const FONT_VAL = 9;
const PAD = 70;

const CATEGORY_COLORS: Record<string, string> = {
  Prem: '#3359ff',
  Ext: '#5b35c0',
  Bev: '#5dca8b',
  BT: '#2f7dbd',
  Val: '#73b5e8',
  Sides: '#fad91b',
  Bund: '#9b59b6',
};
const FALLBACK_COLORS = ['#3359ff', '#5b35c0', '#5dca8b', '#a8b8ff', '#fad91b', '#ff4e4e', '#9b59b6'];

const formatPeso = (v: number) => `₱ ${v.toLocaleString('en-US', { maximumFractionDigits: 0 })}`;

// ─── Geometry ─────────────────────────────────────────────────────────────────

interface IBubble extends IMonitorSalesOosItem {
  x: number;
  y: number;
  rInner: number;
  rOuter: number;
  color: string;
  angle: number;
}

function computeBubbles(items: IMonitorSalesOosItem[]): IBubble[] {
  if (!items.length) {
    return [];
  }

  const days = items.map((d) => d.daysOutOfStock);
  const minD = Math.min(...days);
  const maxD = Math.max(...days);
  const dRange = maxD - minD || 1;
  const n = items.length;
  const dist = (INNER_MAX / Math.sin(Math.PI / n)) * 1.12;

  return items.map((item, i) => {
    const norm = (item.daysOutOfStock - minD) / dRange;
    const rInner = INNER_MIN + norm * (INNER_MAX - INNER_MIN);
    const rOuter = rInner * OUTER_SCALE;
    const angle = ((2 * Math.PI) / n) * i - Math.PI / 2;
    const x = DCX + Math.cos(angle) * dist;
    const y = DCY + Math.sin(angle) * dist;
    const color = CATEGORY_COLORS[item.category] ?? FALLBACK_COLORS[i % FALLBACK_COLORS.length];
    return { ...item, x, y, rInner, rOuter, color, angle };
  });
}

// ─── Block ────────────────────────────────────────────────────────────────────

interface IComponentProps {
  params?: IMonitorSalesParams;
}

export const OosOpportunityLossBlock: FC<IComponentProps> = ({ params }) => {
  const dispatch = useAppDispatch();
  const { monitorSalesOosModel, monitorSalesOosModelLoading } = useAppSelector((state) => state.monitorSales);
  const [hoveredIndex, setHoveredIndex] = useState<number | null>(null);

  useEffect(() => {
    dispatch(getMonitorSalesOosModel(params));
  }, [params?.storeCode, params?.week]);

  const oosItems = monitorSalesOosModel?.items ?? [];
  const bubbles = computeBubbles(oosItems);

  const getOpacity = (i: number, base = 1) => (hoveredIndex !== null && hoveredIndex !== i ? base * 0.4 : base);

  return (
    <div className="ms-block">
      <div className="ms__header">
        <span className="ff-medium fs-xs">OOS Opportunity Loss (Top-5)</span>
        <WarningCircle />
      </div>

      <div className="ms__body">
        {monitorSalesOosModelLoading ? (
          <Skeleton active paragraph={{ rows: 6 }} title={false} />
        ) : (
          <div>
            <svg
              viewBox={`-${PAD} -${PAD} ${DESIGN + 2 * PAD} ${DESIGN + 2 * PAD}`}
              style={{ width: '100%', height: 'auto', display: 'block' }}
            >
              {/* Outer rings */}
              {bubbles.map((b, i) => (
                <circle
                  key={`ring-${i}`}
                  cx={b.x}
                  cy={b.y}
                  r={b.rOuter}
                  fill={b.color}
                  opacity={getOpacity(i, OUTER_OPACITY)}
                  style={{ transition: 'opacity 0.15s ease' }}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                />
              ))}

              {/* Inner filled circles */}
              {bubbles.map((b, i) => (
                <circle
                  key={`inner-${i}`}
                  cx={b.x}
                  cy={b.y}
                  r={b.rInner}
                  fill={b.color}
                  opacity={getOpacity(i)}
                  style={{ transition: 'opacity 0.15s ease' }}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                />
              ))}

              {/* Product name inside each bubble */}
              {bubbles.map((b, i) => (
                <text
                  key={`name-${i}`}
                  x={b.x}
                  y={b.y}
                  textAnchor="middle"
                  dominantBaseline="middle"
                  fontSize={FONT_NAME}
                  fill="#fff"
                  fontFamily="Poppins-SemiBold, Poppins, sans-serif"
                  fontWeight="600"
                  opacity={getOpacity(i)}
                  style={{ transition: 'opacity 0.15s ease' }}
                  onMouseEnter={() => setHoveredIndex(i)}
                  onMouseLeave={() => setHoveredIndex(null)}
                >
                  {b.productName}
                </text>
              ))}

              {/* Dashed connector lines + external labels */}
              {bubbles.map((b, i) => {
                const dirX = Math.cos(b.angle);
                const dirY = Math.sin(b.angle);

                const lx1 = b.x + dirX * b.rOuter;
                const ly1 = b.y + dirY * b.rOuter;
                const lx2 = lx1 + dirX * LINE_LEN;
                const ly2 = ly1 + dirY * LINE_LEN;

                // Gentle bezier curve (perpendicular offset)
                const cpX = (lx1 + lx2) / 2 - dirY * LINE_LEN * 0.35;
                const cpY = (ly1 + ly2) / 2 + dirX * LINE_LEN * 0.35;

                // Pill center: extends outward from line end
                const pillCX = dirX > 0.25 ? lx2 + PILL_W / 2 + 2 : dirX < -0.25 ? lx2 - PILL_W / 2 - 2 : lx2;
                const textAnchor = dirX > 0.25 ? 'start' : dirX < -0.25 ? 'end' : 'middle';
                const catY = ly2 - 2;
                const pillTop = catY + FONT_CAT + 3;

                return (
                  <g
                    key={`label-${i}`}
                    opacity={getOpacity(i)}
                    style={{ transition: 'opacity 0.15s ease', cursor: 'default' }}
                    onMouseEnter={() => setHoveredIndex(i)}
                    onMouseLeave={() => setHoveredIndex(null)}
                  >
                    <path
                      d={`M ${lx1} ${ly1} Q ${cpX} ${cpY} ${lx2} ${ly2}`}
                      fill="none"
                      stroke="#c4c9d9"
                      strokeWidth={1.5}
                      strokeDasharray="4 3"
                    />
                    <text
                      x={lx2}
                      y={catY}
                      textAnchor={textAnchor}
                      dominantBaseline="auto"
                      fontSize={FONT_CAT}
                      fill="#1a1a2e"
                      fontFamily="Poppins-Medium, Poppins, sans-serif"
                      fontWeight="500"
                    >
                      {b.category}
                    </text>
                    <g className={`ms-oos__label ${hoveredIndex === i ? 'ms-oos__label--active' : ''}`}>
                      <rect x={pillCX - PILL_W / 2} y={pillTop} width={PILL_W} height={PILL_H} rx={PILL_RX} />
                      <text
                        x={pillCX}
                        y={pillTop + PILL_H / 2}
                        textAnchor="middle"
                        dominantBaseline="middle"
                        fontSize={FONT_VAL}
                        fontFamily="Poppins-Regular, Poppins, sans-serif"
                      >
                        {formatPeso(b.potentialSalesLost)}
                      </text>
                    </g>
                  </g>
                );
              })}
            </svg>
          </div>
        )}
      </div>
    </div>
  );
};
